import React, { useCallback, useEffect, useState } from 'react';
import { colors, FONT, radii } from '../../../../theme';
import { S } from '../../dashboardStyles';
import { SS } from '../settingsStyles';
import SettingsScreen from '../SettingsScreen';
import { ConfirmSheet } from '../../Sheets';
import UndoToast from '../UndoToast';
import { api } from '../../../../services/api';

const fmt = (iso) => (iso ? new Date(String(iso).replace(' ', 'T') + 'Z')
  .toLocaleString('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }) : '—');

/**
 * One person, everything about them, and the things that can be done to them.
 *
 * Ordered by cost: the note and the role are reversible and live at the top;
 * blocking is reversible but ends sessions, so it sits below a line; deleting
 * is at the bottom, needs the account blocked first, and asks with the
 * consequences spelled out.
 *
 * Deleting requires blocking first for a reason that is not bureaucracy: it
 * makes the destructive path two deliberate acts on two different days if need
 * be, and blocking already achieves what "delete" is usually reached for.
 */
export default function AdminUserCard({ userId, onBack, onDeleted, currentUserId }) {
  const [card, setCard] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [password, setPassword] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [hold, setHold] = useState(0);
  // Blocking is reversible, so the way back is offered where the mistake is
  // easiest to notice — here, for a few seconds.
  const [undo, setUndo] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.adminGetUser(userId);
      setCard(r);
      setNote(r.admin_note || '');
    } catch (e) { setError(e.message || 'Не удалось загрузить'); }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, after) => {
    setBusy(true); setError('');
    try { const r = await fn(); after?.(r); await load(); }
    catch (e) { setError(e.message || 'Не получилось'); }
    finally { setBusy(false); }
  };

  if (!card) {
    return (
      <SettingsScreen title="Пользователь" onBack={onBack}>
        <div style={{ ...SS.consequence, padding: 16, color: error ? colors.red : colors.gray600 }}>
          {error || 'Загружаю…'}
        </div>
      </SettingsScreen>
    );
  }

  const self = card.id === currentUserId;

  return (
    <SettingsScreen title={card.admin_note || card.display_name || '@' + card.username}
      onBack={onBack} scrollKey={`admin-user-${userId}`}>
      {error && <div style={{ ...SS.consequence, color: colors.red, padding: '0 16px 8px' }}>{error}</div>}

      <div style={S.settingsCard}>
        <div style={S.settingsRow}>
          <span style={S.settingsLabel}>Логин</span>
          <span style={{ ...S.settingsValue, fontFamily: 'monospace' }}>@{card.username}</span>
        </div>
        <div style={S.settingsRow}>
          <span style={S.settingsLabel}>SIP номер</span>
          <span style={S.settingsValue}>{card.sip_extension}</span>
        </div>
        <div style={S.settingsRow}>
          <span style={S.settingsLabel}>Заведён</span>
          <span style={S.settingsValue}>{fmt(card.created_at)}</span>
        </div>
        <div style={S.settingsRow}>
          <span style={S.settingsLabel}>Последний вход</span>
          <span style={S.settingsValue}>
            {card.online ? 'в сети' : card.last_seen ? fmt(card.last_seen) : 'ни разу'}
          </span>
        </div>
        <div style={{ ...S.settingsRow, borderBottom: 'none' }}>
          <span style={S.settingsLabel}>Файлы</span>
          <span style={S.settingsValue}>
            {Math.round((card.disk_bytes || 0) / 1024)} КБ · ссылок: {card.active_links || 0}
          </span>
        </div>
      </div>

      {/* The note is why a hundred rows are searchable at all. */}
      <div style={S.settingsCard}>
        <div style={{ padding: '12px 16px' }}>
          <div style={{ ...SS.consequence, marginBottom: 6 }}>
            Заметка видна только администраторам и участвует в поиске
          </div>
          <input value={note} onChange={e => setNote(e.target.value)}
            onBlur={() => note !== (card.admin_note || '') && act(() => api.adminPatchUser(userId, { admin_note: note }))}
            placeholder="Например: Бухгалтерия, Анна"
            style={{ ...S.input, width: '100%', boxSizing: 'border-box', minHeight: 44 }} />
        </div>
      </div>

      <div style={S.sectionLabel}>Устройства</div>
      <div style={S.settingsCard}>
        {(card.devices || []).length === 0 && (
          <div style={{ ...SS.consequence, padding: 16 }}>Устройств нет — человек ещё не входил</div>
        )}
        {(card.devices || []).map((d, i) => (
          <div key={d.device_id} style={{
            ...S.settingsRow, borderBottom: i < card.devices.length - 1 ? undefined : 'none',
          }}>
            <span style={S.settingsLabel}>{d.device_label || 'устройство'}</span>
            <span style={{ ...S.settingsValue, fontFamily: 'monospace', fontSize: 12.5 }}>
              {String(d.device_id).slice(0, 8)} · {fmt(d.last_seen)}
              {d.revoked_at ? ' · отозвано' : ''}
            </span>
          </div>
        ))}
      </div>

      {!self && (
        <div style={SS.dangerZone}>
          <div style={{ ...SS.consequence, padding: '0 16px 8px' }}>
            {card.disabled
              ? `Заблокирован ${fmt(card.disabled_at)}. Вход, звонки и SIP отключены.`
              : 'Блокировка закрывает вход, обрывает открытые соединения и снимает SIP-регистрацию. Переписка остаётся на месте.'}
          </div>

          <button disabled={busy}
            onClick={() => (card.disabled
              ? act(() => api.adminEnableUser(userId))
              : setConfirm({ kind: 'disable' }))}
            style={{
              ...S.textBtn, minHeight: 48, width: '100%', textAlign: 'left', padding: '0 16px',
              color: card.disabled ? colors.gray900 : colors.red,
            }}>
            {card.disabled ? 'Разблокировать' : 'Заблокировать'}
          </button>

          <button disabled={busy} onClick={() => setConfirm({ kind: 'password' })}
            style={{ ...S.textBtn, minHeight: 48, width: '100%', textAlign: 'left', padding: '0 16px' }}>
            Сбросить пароль
          </button>

          <button disabled={busy}
            onClick={() => act(() => api.adminPatchUser(userId, { role: card.role === 'admin' ? 'user' : 'admin' }))}
            style={{ ...S.textBtn, minHeight: 48, width: '100%', textAlign: 'left', padding: '0 16px' }}>
            {card.role === 'admin' ? 'Снять права администратора' : 'Сделать администратором'}
          </button>

          {/* Only for a blocked account, and only with a press held long
              enough to be a decision rather than a slip. */}
          <button
            disabled={busy || !card.disabled}
            onPointerDown={() => {
              if (!card.disabled) return;
              const started = Date.now();
              const timer = setInterval(() => {
                const p = Math.min(1, (Date.now() - started) / 2000);
                setHold(p);
                if (p >= 1) {
                  clearInterval(timer);
                  setHold(0);
                  act(() => api.adminDeleteUser(userId), () => onDeleted?.());
                }
              }, 50);
              const stop = () => { clearInterval(timer); setHold(0); window.removeEventListener('pointerup', stop); };
              window.addEventListener('pointerup', stop);
            }}
            style={{
              ...SS.dangerLink, opacity: card.disabled ? 1 : 0.4,
              background: hold ? `linear-gradient(to right, rgba(221,122,92,0.25) ${hold * 100}%, transparent ${hold * 100}%)` : 'none',
            }}>
            {card.disabled ? (hold ? 'Не отпускайте…' : 'Удалить навсегда — держите нажатой') : 'Удалить можно только заблокированного'}
          </button>
        </div>
      )}

      {password && (
        <div style={{ ...S.settingsCard, padding: 16, marginTop: 12 }}>
          <div style={{ fontFamily: FONT, fontSize: 15, fontWeight: 600 }}>Новый пароль</div>
          <div style={{ ...SS.consequence, marginBottom: 8 }}>Показывается один раз</div>
          <div style={{ fontFamily: 'monospace', fontSize: 16, color: colors.gray900 }}>{password}</div>
          <button onClick={() => { navigator.clipboard?.writeText(password); setPassword(null); }}
            style={{ ...S.primaryBtn, width: '100%', marginTop: 12 }}>Скопировать и закрыть</button>
        </div>
      )}

      {confirm?.kind === 'disable' && (
        <ConfirmSheet
          title={`Заблокировать ${card.admin_note || '@' + card.username}?`}
          consequences={[
            'Вход перестанет работать, открытые сессии закроются немедленно',
            'Звонки и уведомления перестанут приходить — пуш-токены удаляются',
            'SIP-регистрация снимается, номер перестаёт отвечать',
            'Переписка и файлы остаются на месте — блокировку можно снять',
          ]}
          confirmLabel="Заблокировать"
          busy={busy}
          onConfirm={() => {
            setConfirm(null);
            act(() => api.adminDisableUser(userId), () => setUndo(true));
          }}
          onClose={() => setConfirm(null)}
        />
      )}

      {undo && (
        <UndoToast text="Пользователь заблокирован"
          onAction={() => { setUndo(false); act(() => api.adminEnableUser(userId)); }}
          onExpire={() => setUndo(false)} />
      )}

      {confirm?.kind === 'password' && (
        <ConfirmSheet
          title="Сбросить пароль?"
          consequences={[
            'Старый пароль перестанет работать немедленно',
            'Все входы этого человека завершатся — на всех устройствах',
            'Новый пароль покажется один раз, передайте его лично',
          ]}
          confirmLabel="Сбросить пароль"
          busy={busy}
          onConfirm={() => {
            setConfirm(null);
            act(() => api.adminResetPassword(userId), (r) => setPassword(r?.password || null));
          }}
          onClose={() => setConfirm(null)}
        />
      )}
    </SettingsScreen>
  );
}
